/**
 * Wiederkehrende Aufgaben (F-24) - rein und getestet.
 *
 * Eine Serie ist EINE Aufgabe, die immer fuer ihr gerade faelliges Vorkommen
 * steht. So erscheint sie ueberall (Liste, Tagesplan, Kalender, Uebersicht)
 * genau einmal und nie mit allen kuenftigen Vorkommen auf einmal.
 *
 * Abhaken legt eine erledigte Kopie fuer das Vorkommen an und rueckt die
 * Serie weiter; Deadline und Plantag wandern mit dem gleichen Abstand mit.
 * Verpasste Vorkommen stapeln sich nicht: die Serie bleibt einmal ueberfaellig
 * und zaehlt, wie oft sie verpasst wurde.
 */
import { naechstesVorkommen, vorkommenZwischen } from '@/lib/recurrence/aufgaben';
import { addDays, differenceInDays } from '@/lib/datetime';
import type { AufgabenWiederholung, LocalDate, Task } from '@/domain/types';

/** Serie anlegen: Anker ist der Plantag, sonst die Deadline, sonst heute. */
export function wiederholungAnlegen(rrule: string, anker: LocalDate): AufgabenWiederholung | null {
  const vorkommen = naechstesVorkommen({ rrule, start: anker }, anker, true);
  return vorkommen ? { rrule, start: anker, vorkommen } : null;
}

/** Plantag und Deadline fuer ein anderes Vorkommen - mit demselben Abstand. */
export function datenFuer(t: Pick<Task, 'plannedDate' | 'dueDate' | 'wiederholung'>, neu: LocalDate): Pick<Task, 'plannedDate' | 'dueDate'> {
  const alt = t.wiederholung?.vorkommen;
  if (!alt) return { plannedDate: t.plannedDate, dueDate: t.dueDate };
  const schiebe = (d?: LocalDate) => (d ? addDays(neu, differenceInDays(alt, d)) : undefined);
  return { plannedDate: schiebe(t.plannedDate), dueDate: schiebe(t.dueDate) };
}

/** Wie oft die Serie schon verpasst wurde: Vorkommen vor heute, die offen blieben. */
export function verpasst(t: Pick<Task, 'wiederholung' | 'status'>, heute: LocalDate): number {
  const w = t.wiederholung;
  if (!w || t.status === 'erledigt' || w.vorkommen >= heute) return 0;
  return vorkommenZwischen(w, w.vorkommen, addDays(heute, -1)).length;
}

export interface AbhakPlan {
  /** Das Vorkommen, das als erledigt gilt. */
  erledigt: LocalDate;
  /** Das naechste offene Vorkommen, `null` am Ende der Serie. */
  naechstes: LocalDate | null;
}

/**
 * Was beim Abhaken passiert. Liegen verpasste Vorkommen zurueck, zaehlt das
 * Abhaken fuer das juengste bis heute - wer heute Vokabeln lernt, holt nicht
 * drei Tage nach. Ein kuenftiges Vorkommen darf man vorab erledigen.
 */
export function abhakPlan(w: AufgabenWiederholung, heute: LocalDate): AbhakPlan {
  const erledigt = w.vorkommen > heute
    ? w.vorkommen
    : vorkommenZwischen(w, w.vorkommen, heute).at(-1) ?? w.vorkommen;
  return { erledigt, naechstes: naechstesVorkommen(w, erledigt, false) };
}

/** Das Vorkommen nach dem aktuellen - fuer "nur dieses ueberspringen". */
export function nachDemAktuellen(w: AufgabenWiederholung): LocalDate | null {
  return naechstesVorkommen(w, w.vorkommen, false);
}
